<template>
  <div :key="`comparisonwaterfall-${isSidebarShown}`" class="h-100 d-flex flex-column">
    <ReportingSection :short-view="shortView">
      <template #header>
        <ReportingHeader :short-view="shortView" :section="section" :title="tkey('title')">
          <template #metric>
            <v-select
              v-model="reportingStore.metricValue"
              :items="allowedMetrics"
              :disabled="isComparisonWaterfallReportDisabled"
              item-title="text"
              item-value="type"
              :menu-props="{
                contentClass: 'options',
                location: 'bottom center',
              }"
              :placeholder="t('general.select')"
              hide-details
              single-line
              style="max-width: 240px"
              class="mr-3 filter-select rtls-select select"
              bg-color="white"
              :menu-icon="null"
              @update:model-value="value => onSelectionChanged('metricValue', value)"
            >
              <template #append-inner>
                <v-icon role="img" aria-hidden="false" size="10" icon="rtls:BaseIconExpandMore" />
              </template>
              <template #item="data">
                <v-list-item v-bind="data.props" class="slot-item" />
              </template>
            </v-select>
          </template>
          <template #filter>
            <AdvancedFilters
              :filters="reportingStore.filters[section]"
              :filter-options="reportingStore.filterOptions"
              :disabled="isComparisonWaterfallReportDisabled || filtersDisabled"
              :btn-text="filterButtonText"
              class="ml-3"
              @change="handleFilterChange"
              @apply="fetchData"
            />
          </template>
        </ReportingHeader>
      </template>
      <template #main-section>
        <ReportingMainSection
          :section="section"
          :short-view="shortView"
          :subtitle="tkey('subtitle')"
        >
          <template #data-section>
            <span v-if="isComparisonWaterfallReportDisabled" class="not-available">
              {{ t('reportingPage.noPermissions') }}
            </span>
            <BaseProgressBar
              v-else-if="isLoading || isProcessingData"
              :message="t('reportingPage.loadingChart')"
              :style="{ margin: 'auto' }"
              class="pt-5"
            />
            <span v-else-if="hasInvalidComparison">
              {{ tkey('invalidComparison') }}
            </span>
            <span v-else-if="hasInvalidComparisonSelections" class="not-available">
              {{ t('reportingPage.notAvailableForComparisons') }}
            </span>
            <span v-else-if="hasInvalidMetric">
              {{ tkey('invalidMetric') }}
            </span>
            <template v-else>
              <div class="chart-wrapper" :style="{ width: shortView ? '100%' : '50%' }">
                <highcharts :options="chartOptions" class="chart" />
              </div>
              <v-col v-if="!shortView" class="d-flex mx-5 pl-2 h-100 rtls-border rtls-border--left">
                <AgGridVue
                  animate-rows
                  dense
                  class="w-100"
                  :column-defs="headers"
                  :row-data="tableData"
                  :grid-options="gridOptions"
                  @grid-ready="onGridReady"
                />
              </v-col>
            </template>
          </template>
        </ReportingMainSection>
      </template>
      <template #footer>
        <ReportingFooter
          :short-view="shortView"
          :is-export-disabled="!hasAvailableChartData"
          @export="exportCSV"
        />
      </template>
    </ReportingSection>
  </div>
</template>

<script>
import to from 'await-to-js';
import {
  isEqual,
  pick,
  isEmpty,
  omit,
  startsWith,
  find,
  endsWith,
  size,
  map,
  every,
  get,
} from 'lodash';

// plugins and utils
import owColours from '@/plugins/ow-colors';
import defaultChartOptions from '@/plugins/default-chart-options';
import agGridUtils from '@/utils/ag-grid-utils';

// enums
import reportingMetrics from '@enums/reporting-metrics';
import reportingSections from '@enums/reporting-sections';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useExportCsv } from '@/composables/use-export-csv';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useContextStore } from '@/stores/context';
import { useReportingStore } from '@/stores/reporting';

const CHARTS_LOOKUP = {
  [reportingMetrics.metrics.sales]: {
    numberFormat: 'currency',
    field: 'totalSales',
  },
  [reportingMetrics.metrics.margin]: {
    numberFormat: 'currency',
    field: 'totalMargin',
  },
  [reportingMetrics.metrics.volume]: {
    numberFormat: 'float',
    field: 'totalVolume',
  },
  [reportingMetrics.metrics.products]: {
    numberFormat: 'integer',
    field: 'totalProducts',
  },
  [reportingMetrics.metrics.stockingPoints]: {
    numberFormat: 'float',
    field: 'totalPointOfDistribution',
  },
  [reportingMetrics.metrics.linearSpace]: {
    numberFormat: 'float',
    field: 'totalSize',
  },
  [reportingMetrics.metrics.cubicSpace]: {
    numberFormat: 'float',
    field: 'totalSize',
  },
  [reportingMetrics.metrics.frontalSpace]: {
    numberFormat: 'float',
    field: 'totalSize',
  },
  [reportingMetrics.metrics.horizontalSpace]: {
    numberFormat: 'float',
    field: 'totalSize',
  },
};

export default {
  props: {
    // represents whether this report is open on (short) overview page
    shortView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { t, tkey } = useInternationalization('reportingPage.sections.comparison-waterfall');
    const { canViewComparisonWaterfallReport } = usePermission();
    const { formatNumber } = useFormatNumber();
    const { getFileName } = useExportCsv();
    const contextStore = useContextStore();
    const reportingStore = useReportingStore();

    return {
      // i18n
      t,
      tkey,
      // permissions
      canViewComparisonWaterfallReport,
      // formatters
      formatNumber,
      // csv utils
      getFileName,
      // stores
      contextStore,
      reportingStore,
    };
  },

  data() {
    return {
      tableData: [],
      chartData: [],
      totalBaseline: 0,
      totalSumInComparison: 0,
      totalComparison: 0,
      isProcessingData: true,
      section: reportingSections.comparisonWaterfall,
      gridApi: null,
      gridOptions: {
        columnMenu: 'legacy',
        rowHeight: 30,
        headerHeight: 40,
        suppressContextMenu: true,
        defaultColDef: {
          editable: false,
          sortable: false,
          filter: false,
          menuTabs: [],
          suppressMovable: true, // do not make headers movable
        },
      },
      fullChartAxis: [
        {
          color: owColours.reports.baseline,
          field: 'baseline',
          showInLegend: true,
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'newProduct',
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'newInCluster',
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'increasedDistribution',
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'maintainedDistributionUp',
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'maintainedDistributionDown',
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'decreasedDistribution',
        },

        {
          color: owColours.comparisonWaterfall.distributionBar,
          field: 'removedFromCluster',
        },
        {
          isSum: true,
          color: owColours.reports.comparison,
          showInLegend: true,
        },
      ],
      shortChartAxis: [
        {
          color: owColours.reports.baseline,
          field: 'baseline',
          showInLegend: true,
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          fields: ['newProduct', 'newInCluster'],
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          fields: ['increasedDistribution'],
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          fields: ['maintainedDistributionUp', 'maintainedDistributionDown'],
        },
        {
          color: owColours.comparisonWaterfall.distributionBar,
          fields: ['decreasedDistribution', 'removedFromCluster'],
        },
        {
          isSum: true,
          color: owColours.reports.comparison,
          showInLegend: true,
        },
      ],
      notAllowedMetrics: [
        'products',
        'linearSpace',
        'cubicSpace',
        'horizontalSpace',
        'frontalSpace',
      ],
    };
  },

  computed: {
    isComparisonWaterfallReportDisabled() {
      return !this.canViewComparisonWaterfallReport;
    },

    isLoading() {
      return this.reportingStore.loadingSubsection.comparisonWaterfallTable;
    },

    hasInvalidComparisonSelections() {
      // user must select both baseline and comparison in order to have a waterfall result
      return (
        (!this.reportingStore.isReportDataStale && !!size(this.reportingStore.comparison2Ids)) ||
        this.reportingStore.isAnySelectionObserved
      );
    },

    hasAvailableChartData() {
      return (
        !isEmpty(this.chartData) &&
        !!size(this.reportingStore.baselineIds) &&
        !!size(this.reportingStore.comparisonIds)
      );
    },

    isSidebarShown() {
      return this.reportingStore.isSidebarShown;
    },

    cacheKey() {
      // Create a composite key for report cache from section, metric and filters
      const filtersKey = this.reportingStore.filters[this.section]
        .map(f => {
          const operator = f.notEqualTo ? 'neq' : 'eq';
          const filterValuesStr = f.filterValues
            .map(v => v.name)
            .sort()
            .join(',');
          return `${f.filterName}:${operator}:${filterValuesStr}`;
        })
        .sort()
        .join('|');
      return `${this.section}_${this.reportingStore.metricValue}_${filtersKey}`;
    },

    chartOptions() {
      const vm = this;
      const numberFormatter = number =>
        this.formatNumber({
          number,
          format: CHARTS_LOOKUP[this.reportingStore.metricValue].numberFormat,
        });

      return {
        ...defaultChartOptions,
        chart: {
          type: 'waterfall',
          style: {
            fontFamily: 'Source Sans Pro Regular, Source Sans Pro, sans-serif',
            fontSize: '15px',
          },
        },

        title: { text: null },

        xAxis: this.getxAxis(),
        legend: { enabled: false },
        credits: { enabled: false },
        yAxis: {
          title: { text: this.shortView ? this.metricText : this.tkey('chart.yAxis') },
          labels: {
            enabled: true,
            style: { color: owColours.comparisonWaterfall.chartFontColor },
          },

          plotLines: isEmpty(this.chartData)
            ? []
            : [
                {
                  value: this.totalBaseline,
                  width: 2,
                  color: owColours.comparisonWaterfall.plotLine.baseline,
                },
                {
                  value: this.totalComparison,
                  width: 2,
                  color: owColours.comparisonWaterfall.plotLine.comparison,
                },
              ],
        },

        tooltip: {
          useHTML: true,
          formatter() {
            return `<div style="border-color: ${this.series.color};"><b>${vm.metricText}</b>:${numberFormatter(this.y)}</div>`;
          },
          backgroundColor: '#ffffff8c',
          // borderColor: 'blue',
          borderWidth: '1px',
          style: { fontSize: '12px' },
        },

        series: this.chartData,
      };
    },

    hasInvalidComparison() {
      // Do nothing if report data is stale
      if (this.reportingStore.isReportDataStale) return false;

      const fieldsToCompare = ['scenarioId', 'storeClassId', 'clusterId'];
      const baseline =
        this.reportingStore.getSelectionFromId(this.reportingStore.baselineIds) || [];
      const comparison =
        this.reportingStore.getSelectionFromId(this.reportingStore.comparisonIds) || [];

      // All baseline selections should have the same fields to compare
      const baselineFields = map(baseline, b => pick(b, fieldsToCompare));
      const hasValidBaselines = every(baselineFields, bf => isEqual(bf, baselineFields[0]));
      if (!size(baselineFields) || !hasValidBaselines) return true;

      // All comparison selections should have the same fields to compare
      const comparisonFields = map(comparison, c => pick(c, fieldsToCompare));
      const hasValidComparisons = every(comparisonFields, cf => isEqual(cf, comparisonFields[0]));
      if (!size(comparisonFields) || !hasValidComparisons) return true;

      return !isEqual(baselineFields[0], comparisonFields[0]);
    },

    metricText() {
      return find(this.reportingStore.enabledMetrics, { type: this.reportingStore.metricValue })
        .text;
    },

    headers() {
      return [
        {
          headerName: this.tkey('table.productKey'),
          minWidth: 80,
          resizable: true,
          field: 'productKeyDisplay',
          menuTabs: ['filterMenuTab'],
          filter: true,
        },
        {
          headerName: this.tkey('table.description'),
          minWidth: 100,
          field: 'description',
          width: 400,
          resizable: true,
        },
        {
          headerName: this.tkey('table.classification'),
          minWidth: 150,
          field: 'classification',
          resizable: true,
          menuTabs: ['filterMenuTab'],
          filter: true,
          valueFormatter: params => this.getClassification(params),
          filterParams: { valueFormatter: params => this.getClassification(params) },
        },
        {
          headerName: this.tkey('table.storeCount'),
          minWidth: 50,
          resizable: true,
          field: 'storeCount',
          valueFormatter: params => {
            return this.formatNumber({ number: params.value, format: 'float' });
          },
        },
        {
          headerName: `Δ ${this.metricText}`,
          field: 'metricValue',
          minWidth: 100,
          resizable: true,
          valueFormatter: params => {
            return this.formatNumber({
              number: params.value,
              format: CHARTS_LOOKUP[this.reportingStore.metricValue].numberFormat,
            });
          },
        },
      ];
    },

    allowedMetrics() {
      return this.reportingStore.enabledMetrics.filter(
        m => !this.notAllowedMetrics.includes(m.type)
      );
    },

    hasInvalidMetric() {
      // Currently selected metric must be one of the allowed metrics
      return (
        !this.reportingStore.isReportDataStale &&
        this.notAllowedMetrics.includes(this.reportingStore.metricValue)
      );
    },

    filtersDisabled() {
      return !this.hasAvailableChartData && this.isLoading;
    },

    filterButtonText() {
      return `${this.t('reportingPage.filters', this.reportingStore.filters[this.section].length)}`;
    },

    visibleAxis() {
      if (this.shortView) return this.shortChartAxis;

      return this.fullChartAxis;
    },
  },

  watch: {
    'reportingStore.reportsGenerated': function () {
      this.fetchData();
    },
  },

  mounted() {
    // fetch data on mount
    this.fetchData();
  },

  methods: {
    async fetchData() {
      // user must select both baseline and comparison in order to have a waterfall result
      if (
        this.hasInvalidComparisonSelections ||
        this.hasInvalidComparison ||
        this.hasInvalidMetric
      ) {
        this.isProcessingData = false;
        return;
      }

      const cachedData = get(this.reportingStore.reportData, this.cacheKey);

      const dataPromise = cachedData
        ? Promise.resolve(cachedData)
        : this.reportingStore.fetchReportingData({
            section: this.section,
            reportArea: 'table',
            productFilters: this.reportingStore.filters[this.section],
          });

      this.isProcessingData = true;
      const [err, response] = await to(dataPromise);

      this.reportingStore.updateReportData(this.section, response, this.cacheKey);

      this.tableData = err ? [] : response.tableData;
      this.chartData = err ? [] : this.getSeries(response.chartData);
      this.totalBaseline = err ? 0 : response.chartData.baseline;
      this.totalComparison = err ? 0 : response.chartData.comparison;
      this.isProcessingData = false;
    },

    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
    },

    getSeries(chartData) {
      const data = this.visibleAxis.map(axis => {
        // is comparison area
        if (axis.isSum) return axis;
        let y = chartData[axis.field];
        if (Array.isArray(axis.fields)) {
          y = axis.fields.reduce((total, field) => total + chartData[field], 0);
        }

        return {
          ...omit(axis, ['field', 'fields']),
          y,
        };
      });

      const numberFormatter = number => this.formatNumber({ number, format: 'numberShorthand' });

      const dataLabels = {
        enabled: true,
        style: { fontSize: '11px' },
        formatter() {
          return `${numberFormatter(this.y)}`;
        },
      };

      return [{ data, pointPadding: 0, showInLegend: true, dataLabels, borderRadius: 0 }];
    },

    async onSelectionChanged(field, value) {
      await this.reportingStore.setSelection({ field, value });
      // If previously we had an invalid selection then we should fetch data
      if (!this.hasInvalidComparison || !this.hasInvalidComparisonSelections) {
        await this.fetchData();
      }
      this.navigateToNewUrl();
    },

    // include new query to url param
    navigateToNewUrl() {
      const newQueryValues = {
        ...this.$route.query,
        metricValue: this.reportingStore.metricValue,
      };

      this.$router.push({
        path: this.$route.path, // keep on same path
        query: newQueryValues,
      });
    },

    getClassification(params) {
      const baseTranslation = this.tkey(`table.${params.value}`);
      if (startsWith(params.value, 'maintained')) {
        return `${baseTranslation} ${this.metricText}`;
      }
      return baseTranslation;
    },

    handleFilterChange(filters) {
      this.reportingStore.setReportingFilters({ section: this.section, filters });
    },

    getxAxis() {
      const vm = this;
      if (!this.shortView) {
        return [
          {
            categories: [
              this.t('reportingPage.baseline'),
              this.tkey('chart.newProduct'),
              this.tkey('chart.newInCluster'),
              // the next four have added arrows, so we format them differently
              'distributionUp',
              'maintainedUp',
              'maintainedDown',
              'distributionDown',
              this.tkey('chart.removedFromCluster'),
              this.t('reportingPage.comparison'),
            ],
            plotBands: [
              {
                color: owColours.comparisonWaterfall.plotBands.nonMaintained,
                from: 0.6,
                to: 2.4,
              },

              {
                color: owColours.comparisonWaterfall.plotBands.nonMaintained,
                from: 2.6,
                to: 3.4,
              },

              {
                color: owColours.comparisonWaterfall.plotBands.maintained,
                from: 3.6,
                to: 5.4,
              },
              {
                color: owColours.comparisonWaterfall.plotBands.nonMaintained,
                from: 5.6,
                to: 7.4,
              },
            ],
            labels: {
              useHTML: true,
              formatter() {
                let icon = '';
                // add arrows to each axis when allowed
                if (endsWith(this.value, 'Down')) icon = 'arrow-down';
                if (endsWith(this.value, 'Up')) icon = 'arrow-up';
                if (isEmpty(icon)) return this.value;
                let text = vm.tkey(`chart.${this.value}`);
                if (startsWith(this.value, 'maintained')) {
                  text = vm.metricText;
                }

                return `<div><i class="fa fa-${icon}"></i> ${text}</div>`;
              },
              style: {
                fontSize: '11px',
                color: owColours.comparisonWaterfall.chartFontColor,
              },
              rotation: -45,
            },
          },
          {
            linkedTo: 0, // top axis
            categories: [
              '', // each empty space is a break in xAxis
              this.tkey('chart.buckets.modelled'),
              '',
              this.tkey('chart.buckets.increased'),
              '',
              this.tkey('chart.buckets.maintained'),
              '',
              this.tkey('chart.buckets.decreased'),
            ],
            tickPositions: [1, 3, 5, 7],
            tickInterval: 0.5,
            opposite: true, // show four buckets at the top
            labels: {
              style: {
                fontSize: '11px',
                color: owColours.comparisonWaterfall.chartFontColor,
              },
            },
          },
        ];
      }

      return {
        categories: [
          this.t('reportingPage.baseline'),
          this.tkey('chart.buckets.modelled'),
          this.tkey('chart.buckets.increased'),
          this.tkey('chart.buckets.maintained'),
          this.tkey('chart.buckets.decreased'),
          this.t('reportingPage.comparison'),
        ],
        labels: {
          style: {
            fontSize: '11px',
            color: owColours.comparisonWaterfall.chartFontColor,
          },
        },
      };
    },

    getAllExportableDataColumns() {
      return this.gridApi.getAllDisplayedColumns();
    },

    processCellCallback(params) {
      // Being called for each cell

      if (params.column.colId === 'storeCount') {
        return this.formatNumber({ number: params.value, format: 'float' });
      }

      if (params.column.colId === 'metricValue') {
        return this.formatNumber({
          number: params.value,
          format: CHARTS_LOOKUP[this.reportingStore.metricValue].numberFormat,
        });
      }

      if (params.column.colId === 'classification') {
        return this.getClassification(params);
      }

      return agGridUtils.utils.processCellForExport(params);
    },

    exportCSV() {
      const exportParams = {
        fileName: this.getFileName({
          serviceName: 'comparison-waterfall-report',
          fileNameDateFormat: this.contextStore.getDateFormats.csvFileName,
        }),
        suppressQuotes: this.contextStore.getCsvExport.suppressQuotes,
        columnSeparator: this.contextStore.getCsvExport.columnSeparator,
        columnKeys: this.getAllExportableDataColumns(),
        processCellCallback: this.processCellCallback,
      };

      this.gridApi.exportDataAsCsv(exportParams);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.fa-arrow-up) {
  color: $success-colour;
}

:deep(.fa-arrow-down) {
  color: $error-colour;
}

.not-available {
  margin: auto;
}

// Out of flow so the chart's height can't grow its container and trigger an endless reflow loop
.chart-wrapper {
  position: relative;
  flex-shrink: 0;
}

.chart {
  position: absolute;
  inset: 0;
}

.filter-select {
  :deep(.v-icon) {
    opacity: unset;
  }
  :deep(.v-input__control) {
    height: 32px;
  }
  :deep(.v-field__field) {
    height: 32px;
  }
  :deep(.v-field__input) {
    min-height: 32px;
    padding-top: 8px;
  }
  :deep(.v-list) {
    padding: 0 !important;
  }

  :deep(v-field--focused) {
    background-color: red !important;
  }

  :deep(
      .v-field--focused.v-field--variant-filled .v-field__outline::after,
      .v-field--focused.v-field--variant-underlined .v-field__outline::after
    ) {
    border-color: $assortment-primary-colour !important;
  }
}

.rtls-select {
  .v-input__slot,
  .v-select__slot {
    background: white;
    height: 32px !important;
  }
  &.select {
    border-radius: 0px;
    height: 32px;
    .v-input__control {
      min-height: 32px;
    }
  }
}

.slot-item {
  width: 240px !important;
  min-height: 32px !important;
}
</style>
